import { useState } from "react";
import { ExpandOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { ChangeRowProps } from "../../types";
import {
  BLOCK_FIELD_FORMATTERS,
  extractChangeValue,
  isDeferredTextField,
} from "./utils";
import {
  TextDisplay,
  ValueDiff,
} from "@/features/datablocks/details/components/history/displayComponents";
import FieldValueDiffDisplay from "@/features/datablocks/details/components/history/FieldValueDiffDisplay";
import TextDiffModal from "@/features/datablocks/details/components/history/TextDiffModal";

import "../../styles.css";

const HistoryChangeRow = ({ change, lookup }: ChangeRowProps) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const field = change.dataField;
  const label = field?.title || change.fieldName;

  if (!field) {
    const formatter = BLOCK_FIELD_FORMATTERS[change.fieldName];
    const oldText = change.oldValue?.value as string;
    const newText = change.newValue?.value as string;

    return (
      <div className="history-change-box flex flex-col gap-150">
        <div className="history-change-box__header flex items-center justify-between gap-200">
          <span className="body-s-medium">{formatter?.name ?? label}</span>
        </div>
        <ValueDiff<string>
          oldItems={oldText ? [formatter?.value(oldText) ?? oldText] : null}
          newItems={newText ? [formatter?.value(newText) ?? newText] : null}
          Component={TextDisplay}
          showArrow
        />
      </div>
    );
  }

  const oldValue = extractChangeValue({
    field,
    relations: change.oldRelations,
    jsonValue: change.oldValue,
  });

  const newValue = extractChangeValue({
    field,
    relations: change.newRelations,
    jsonValue: change.newValue,
  });

  const needsExpand = isDeferredTextField(field);

  return (
    <>
      <div className="history-change-box flex flex-col gap-150">
        <div className="history-change-box__header flex items-center justify-between gap-200">
          <span className="body-s-medium">{label}</span>

          {needsExpand && (
            <BaseButton
              size={BUTTON_SIZES.EXTRA_SMALL}
              variant={BUTTON_VARIANTS.LINK}
              onClick={() => setModalOpen(true)}
              leadingIcon={<ExpandOutlined />}
            >
              {t("datablocks.labels.expand")}
            </BaseButton>
          )}
        </div>
        <FieldValueDiffDisplay
          field={field}
          oldValue={oldValue}
          newValue={newValue}
          lookup={lookup}
        />
      </div>

      {needsExpand && (
        <TextDiffModal
          open={modalOpen}
          setOpen={setModalOpen}
          title={label}
          oldValue={String(oldValue ?? "")}
          newValue={String(newValue ?? "")}
        />
      )}
    </>
  );
};

export default HistoryChangeRow;
